import { toNumber, forEach, first, get, has } from 'lodash';
import classnames from 'classnames';

import { isTableType } from 'constants/chart-roles';

/**
 * Utility to build an object to track column counts
 * @param {Array} columnDefs - Array of Column definitions
 * @param {Object} channels - Object containing channel information
 * @param {Object} acc - Object to accumulate fields and totals
 * @returns {Object} Object containing fields used and their to-be totals.
 */
function buildColumnCount(columnDefs, channels, acc = {}) {
  columnDefs.forEach(column => {
    if (column.field) {
      // don't attempt to tally groups etc
      if (
        !(
          channels[column.field] &&
          channels[column.field].channelType === 'category'
        )
      ) {
        acc[column.field] = 0;
      }
    } else {
      // a column without a field is a column group
      buildColumnCount(column.children, channels, acc);
    }
  });

  return acc;
}

/**
 * Utility to make sure we can insert a 'rowTotals' field, even if one already exists
 * @param {Object} fieldsObj - Fields object to mutate when we succeed
 * @param {Number} attempt - Incrementing counter to use on future attempts
 * @returns {String} - Name of the field that is inserted
 */
function uniqueRowTotalName(fieldsObj, attempt = 0) {
  const fieldName = attempt === 0 ? 'rowTotals' : `rowTotals_${attempt}`;
  if (has(fieldsObj, fieldName)) {
    return uniqueRowTotalName(fieldsObj, attempt + 1);
  }

  fieldsObj[fieldName] = 0;
  return fieldName;
}

/**
 * Stage which runs through all the couments and generates a tally of row / columns if enabled via customisation.
 * @param {Object} stageData  - An object with documents, an AgGrid spec & (chartType, customisations, channels)
 * @returns {Object} stageData - An updated spec with a row totals column & columns total document
 */
function tableTotals(stageData) {
  const {
    spec,
    documents,
    environment: { chartType, customisations, channels },
  } = stageData;
  const {
    options: { totalsColumn, totalsRow },
  } = customisations;

  const totalsColumnEnabled = get(totalsColumn, 'enabled', true);
  const totalsRowEnabled = get(totalsRow, 'enabled', false);

  // don't apply this stage if it's not a table or if we don't have the right customisation options
  if (!isTableType(chartType) || !(totalsColumnEnabled || totalsRowEnabled)) {
    return {};
  }

  let rowTotalsField;

  const columnTotals = buildColumnCount(spec.columnDefs, channels);
  if (totalsRowEnabled) {
    rowTotalsField = uniqueRowTotalName(columnTotals);
  }

  // generate the new documents (if need be) & calculate totals
  const newDocuments = documents.map(doc => {
    let rowTotal = 0;

    forEach(doc, (docValue, field) => {
      const valueField = get(docValue, 'raw', docValue);
      const value = toNumber(valueField);

      if (has(columnTotals, field)) {
        if (totalsColumnEnabled) {
          columnTotals[field] += value;
        }

        rowTotal += value;
      }
    });

    if (totalsRowEnabled) {
      columnTotals[rowTotalsField] += rowTotal;
      return { ...doc, [rowTotalsField]: rowTotal };
    }

    return doc;
  });

  /**
   * Get the column to template from. If the 'value' column exists, use that.
   * If we can't find it (likely because of dynamic columns), use the first dynamicColumns child.
   */
  const valueColumn = spec.columnDefs.find(col => col.field === 'value');
  const dynamicColumnGroup = spec.columnDefs.find(
    col => col.groupId === 'dynamicColumns'
  );
  const templateColumn =
    valueColumn || (dynamicColumnGroup && first(dynamicColumnGroup.children));

  const rowTotalColumn = {
    ...templateColumn,
    headerName: 'Total',
    headerTooltip: 'Total',
    headerClass: classnames(
      'grid-header-cell-type--numeric',
      'grid-header-cell-type--rowTotals'
    ),
    field: rowTotalsField,
    cellRenderer: 'tableTotalCellRenderer',
    cellStyle: { flex: 1, 'flex-direction': 'row-reverse' },
  };

  // Adding some presentation
  columnTotals.group = 'Total';

  return {
    spec: {
      // add the row total column if we added one
      ...spec,
      columnDefs: [
        ...spec.columnDefs,
        ...(totalsRowEnabled ? [rowTotalColumn] : []),
      ],
      gridOptions: {
        ...spec.gridOptions,
        // add the column totals as a pinned row
        ...(totalsColumnEnabled ? { pinnedBottomRowData: [columnTotals] } : {}),
      },
    },
    documents: newDocuments,
  };
}

export default {
  input: [
    'spec',
    'documents',
    'environment.chartType',
    'environment.channels',
    'environment.customisations.options',
    'environment.customisations.options',
  ],
  fn: tableTotals,
};
export { tableTotals, buildColumnCount, uniqueRowTotalName };
